/**
 * Helpers for MasterSearchTermV2 (wrapper search-term:insights).
 */

import {
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  prepareWrapperFilters,
  SB_SD_OPTIMISATION_TOOLTIP,
  buildWrapperInsightsQueryBody,
} from "../../../helper/insights/insightsFilterUtils";
import {
  SEARCH_TERM_V2_ALWAYS_MEASURES,
  SEARCH_TERM_V2_DEFAULT_DIMENSIONS,
  SEARCH_TERM_V2_PAGE_MEASURES,
} from "./searchTermV2Configs";

export {
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  prepareWrapperFilters,
  SB_SD_OPTIMISATION_TOOLTIP,
};

/** Search Term confirm/action note (includes Broad Match caveat). */
export const SB_SD_OPTIMISATION_MODAL_NOTE =
  "SB/SD rows are not supported and will be ignored. Negative keywords are not supported for Broad Match Type.";

export function normalizeSearchTermInsightRow(row) {
  return {
    ...row,
    isChecked: false,
  };
}

export function normalizeSearchTermInsightRows(rows) {
  return (rows || []).map((row) => normalizeSearchTermInsightRow(row));
}

export function getSearchTermSearchMembers(queryDimensions = []) {
  const dims = [...new Set(queryDimensions || SEARCH_TERM_V2_DEFAULT_DIMENSIONS)];
  return [
    ...new Set([...dims, "portfolio_name", "ad_group_name", "campaign_name"]),
  ];
}

export function buildSearchTermInsightsQueryBody({
  queryDimensions = SEARCH_TERM_V2_DEFAULT_DIMENSIONS,
  filters = {},
  queryMeasure = SEARCH_TERM_V2_PAGE_MEASURES,
  queryOrder = [["spends", "desc"]],
  dateRange = [],
  offset = 0,
  limit = 50,
}) {
  return buildWrapperInsightsQueryBody({
    queryDimensions,
    filters,
    queryMeasure,
    alwaysMeasures: SEARCH_TERM_V2_ALWAYS_MEASURES,
    queryOrder,
    dateRange,
    offset,
    limit,
  });
}

export function toggleSingleSelection(rows, index) {
  return (rows || []).map((row, i) =>
    i === index ? { ...row, isChecked: !row.isChecked } : row
  );
}

export function toggleSelectAll(rows, checked) {
  return (rows || []).map((row) => ({ ...row, isChecked: checked }));
}

/** Build campaign/adgroup option lists for Add-to-Negative modal from wrapper rows. */
export function buildNegativeModalOptionsFromRows(rows = []) {
  const campaignsById = new Map();
  const adgroupsById = new Map();

  (rows || []).forEach((row) => {
    const campaignId = row?.campaign_id;
    const adGroupId = row?.ad_group_id;
    if (campaignId != null && campaignId !== "") {
      const id = String(campaignId);
      if (!campaignsById.has(id)) {
        campaignsById.set(id, {
          id,
          name: row?.campaign_name || id,
          platform_id: campaignId,
        });
      }
    }
    if (adGroupId != null && adGroupId !== "") {
      const id = String(adGroupId);
      if (!adgroupsById.has(id)) {
        adgroupsById.set(id, {
          id,
          name: row?.ad_group_name || id,
          platform_id: adGroupId,
          campaign_id:
            row?.campaign_id != null ? String(row.campaign_id) : undefined,
        });
      }
    }
  });

  return {
    campaigns: Array.from(campaignsById.values()),
    adGroups: Array.from(adgroupsById.values()),
  };
}

export function getTargetingTypeFromSearchTerm(searchTerm) {
  const asinRegex = /^[A-Z0-9]{10}$/i;
  if (asinRegex.test(searchTerm)) return "product";
  return "keyword";
}

export function resolveNegativeTargetType(row) {
  if (row?.targeting?.includes("Keyword")) return "keyword";
  if (row?.targeting?.includes("Product")) return "product";
  return getTargetingTypeFromSearchTerm(row?.search_term);
}
